import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { Calendar, DollarSign } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type PersonProposal = {
  id: string;
  title: string;
  status: string | null;
  value: number | null;
  currency: string | null;
  sent_date: string | null;
  created_at: string;
};

interface ProposalsListProps {
  personId: string;
}

export function ProposalsList({ personId }: ProposalsListProps) {
  const { data: proposals = [], isLoading } = useQuery({
    queryKey: ["person-proposals", personId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposals")
        .select(`
          *,
          proposal_recipients!inner (
            person_id
          )
        `)
        .eq("proposal_recipients.person_id", personId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as PersonProposal[];
    },
    enabled: !!personId
  });

  if (isLoading) {
    return <div className="text-center py-4">Loading proposals...</div>;
  }

  if (proposals.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No proposals found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {proposals.map((proposal) => (
        <Card key={proposal.id}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold mb-2">{proposal.title}</h3>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {proposal.sent_date 
                        ? new Date(proposal.sent_date).toLocaleDateString()
                        : new Date(proposal.created_at).toLocaleDateString()
                      }
                    </span>
                  </div>
                  {proposal.value && (
                    <div className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4" />
                      <span>
                        {proposal.value.toLocaleString()} {proposal.currency || "EUR"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <StatusBadge status={proposal.status || "draft"} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}